import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, EmptyState, ErrorState, FIELD_CLASS, Spinner, cx } from '@/components/ui';
import { formatDateTime, toMoneyDisplay, toMoneyValue } from '@/lib/format';
import { TradingNav } from './TradingNav';
import {
  FX_RANGES,
  dayTicks,
  type FxRange,
  formatRate,
  fxRangeLabel,
  fxRangeSince,
  fxRangeSpan,
  krwToUsd,
  summarize,
  toChartRows,
  toUsdInput,
  usdToKrw,
} from './exchangeRateDisplay';
import { MarketSeriesPanel } from './MarketSeriesPanel';
import { useMarketScope } from './marketScope';
import {
  BOND_HELP,
  MARKET_TABS,
  US_INDEX_HELP,
  marketTabLabel,
  marketTabOf,
  seriesCodesOf,
  type MarketTabKey,
} from './marketSeries';
import { usePaperChartColors } from './paperChartColors';
import { useLiveUsdKrwRate, useUsdKrwHistory } from './useStocks';

/**
 * 환율 - 달러 → 원. (2026-09-19 사용자 요청, I-61)
 *
 * "환율 대메뉴" 로 시작했다가 사용자가 **단타 안의 탭**으로 정했다 -
 * 권한 칸(cm_menu)을 새로 만들지 않고 TRADING 권한으로 연다. 엔화는 넘겼다(토스가 KRW↔USD 만 준다).
 *
 * 화면 셋
 *   지금       토스 표시 환율. 1분마다 다시 읽는다(서버 60초 캐시)
 *   계산기     달러 ↔ 원. 어느 칸이든 적으면 다른 칸이 따라온다
 *   추이       매시 정각 기록(ExchangeRateSyncJob) 7일 · 30일
 *
 * 🔴 참고 환율이다
 *   실제 환전은 토스의 거래 환율(스프레드 · 우대율)로 한다. 계산기 값을 "받는 돈" 으로 읽지 않게 화면이 말한다.
 */
/**
 * 시장지표 — 환율 탭을 넓혔다 (2026-09-29 I-81 · 사용자 결정 "환율 탭을 시장지표로 · 하위 탭 환율 · 비트코인 · 금 · 국고채 · 지수 · 기본 환율").
 *
 * 🔴 경로(/trading/fx)와 이 컴포넌트 이름은 그대로 둔다 — 라우트(App.tsx)는 공유 영역이고, 옛 주소 · 북마크도 그대로 열린다.
 *   하위 탭은 주소의 ?tab= 로 가른다(btc · gold · bond · index · 없으면 환율) — 뒤로 가기 · 새로 고침에도 같은 탭이다
 *
 * ✏️ 2026-10-17 사용자 Q17 — 국고채 · 지수 하위 탭은 위 국내 / 미국 스위치를 따른다(marketSeries.ts US_TABS).
 *   미국이면 국고채 탭 = "미국 국채" 2년 · 10년(미국 재무부) · 지수 탭 = SPY · QQQ ETF 값(토스 미국 일봉 · 사용자 결정 10/17 18:3x)
 *   환율 · 비트코인 · 금은 두 시장이 같다
 */
export function ExchangeRatePage() {
  const [params, setParams] = useSearchParams();
  const scope = useMarketScope((s) => s.scope);
  const tab = marketTabOf(params.get('tab'));
  const codes = seriesCodesOf(tab, scope);
  const intro = tab === 'fx' ? '' : ((scope === 'US' ? MARKET_INTRO_US[tab] : undefined) ?? MARKET_INTRO[tab]);
  // 물음표 — 국채는 두 시장 공통 설명 · 지수는 미국(ETF 값)일 때만(국내 지수 탭은 그대로 둔다 · 사용자 10/17)
  const help = tab === 'bond' ? BOND_HELP : tab === 'index' && scope === 'US' ? US_INDEX_HELP : undefined;

  return (
    <div className="flex flex-col gap-4">
      <TradingNav />

      <header>
        <h1 className="text-xl font-semibold">시장지표</h1>
      </header>

      <div className="flex flex-wrap gap-1 border-b border-line pb-2" role="tablist" aria-label="시장지표">
        {MARKET_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setParams(t.key === 'fx' ? {} : { tab: t.key }, { replace: true })}
            className={cx(
              'min-h-[36px] rounded-lg px-3 py-1.5 text-sm font-medium',
              tab === t.key ? 'bg-brand text-brand-fg' : 'text-fg-muted hover:bg-surface-hover',
            )}
          >
            {marketTabLabel(t.key, scope)}
          </button>
        ))}
      </div>

      {tab === 'fx' ? (
        <FxPanel />
      ) : (
        <MarketSeriesPanel codes={codes} intro={intro} help={help} />
      )}
    </div>
  );
}

/**
 * 미국 모드의 하위 탭 머리 문장 (2026-10-17) — 여기 없는 탭은 MARKET_INTRO 를 그대로 쓴다(두 시장이 같다)
 */
const MARKET_INTRO_US: Partial<Record<Exclude<MarketTabKey, 'fx'>, string>> = {
  bond: '미국 정부가 빌린 돈의 이자율(미국 국채 수익률)입니다. 2년은 가까운 몇 년의 기준금리 예상을, 10년은 주택담보대출 같은 장기 금리의 기준을 비춥니다. 금리가 오르면 채권값은 내립니다.',
  index: '미국 주식시장 전체의 흐름을 지수를 따르는 ETF 값(달러)으로 봅니다. SPY 는 S&P 500(대형주 500곳), QQQ 는 나스닥 100(나스닥의 큰 회사 100곳)을 따릅니다. 지수 포인트가 아닙니다.',
};

/** 하위 탭 머리 문장 — 무엇을 보는지 비전문가 말로(메모리 explain-jargon-in-ui) */
const MARKET_INTRO: Record<Exclude<MarketTabKey, 'fx'>, string> = {
  btc: '비트코인 1개가 몇 원인지 봅니다. 업비트 원화 시장 값이고, 하루는 오전 9시에 넘어갑니다.',
  gold: '금 1g이 몇 원인지 봅니다. 한국거래소 금시장의 순도 99.99% · 1kg 상품 종가입니다.',
  bond: '나라가 빌린 돈의 이자율(국고채 금리)입니다. 금리가 오르면 채권값은 내리고, 예 · 적금 금리도 대개 따라 움직입니다.',
  index: '한국 주식시장 전체의 흐름입니다. 코스피는 대형주 시장, 코스닥은 중소 · 기술주 시장의 지수입니다.',
};

/** 환율 하위 탭 — 전의 환율 화면 그대로(I-61 · I-66) */
function FxPanel() {
  const live = useLiveUsdKrwRate();
  const [range, setRange] = useState<FxRange>(7);
  const history = useUsdKrwHistory(range);

  const rate = live.data?.rate ?? null;
  const summary = summarize(history.data ?? [], rate);

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-fg-muted">
        1달러가 몇 원인지 봅니다. 토스증권이 보여주는 <strong>참고 환율</strong>이라, 실제로
        환전할 때 적용되는 환율(수수료 · 우대율 포함)과는 조금 다를 수 있습니다.
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="지금 달러 환율">
          {live.isPending ? (
            <Spinner />
          ) : live.isError ? (
            <ErrorState message="환율을 불러오지 못했습니다." onRetry={() => live.refetch()} />
          ) : rate === null ? (
            <EmptyState
              title="환율을 가져오지 못했습니다"
              /*
                🔴 2026-09-20 — 원인 설명이 틀려 있었다
                  이 카드가 읽는 것은 /exchange-rate/live 이고, 그 API 는 토스를 직접 부른다.
                  매시 배치와 무관하게 토스가 답하면 즉시 보인다.
                  즉 여기까지 오는 경우는 "토스 호출 실패 **그리고** 저장된 이력도 없음" 뿐이다.
                  "매시 정각에 처음 기록된 뒤부터" 는 기다리면 된다는 뜻이라 오도한다.
              */
              description="토스에서 현재 환율을 받지 못했고 저장된 기록도 없습니다. 잠시 뒤 다시 열어 보세요."
            />
          ) : (
            <div className="flex flex-col gap-2">
              <p className="text-3xl font-semibold tabular-nums">{formatRate(rate)}</p>
              <p className="text-xs text-fg-subtle">
                1달러 기준 · {formatDateTime(live.data?.fetchedAt)} · 1분마다 새로 읽습니다
              </p>
              {summary && (
                /*
                  한 줄에 하나씩 - 두 칸으로 나누면 PC 절반 폭 카드에서 "7일 / 전보다" 가 꺾였다.
                  이름(dt)은 줄바꿈하지 않고, 값(dd)이 길면 값만 다음 줄로 흐른다.
                */
                <dl className="mt-1 flex flex-col gap-1 text-sm">
                  <div className="flex flex-wrap gap-x-2">
                    <dt className="shrink-0 text-fg-muted">{fxRangeSince(range)}</dt>
                    {/*
                      🔴 등락색을 붙인다 (2026-09-20 사용자 지시)
                        이 앱은 오르면 빨강(--rise) · 내리면 파랑(--fall)이다. 주식 화면 전체가
                        그 규칙을 쓰는데 이 줄만 검은 글자였다. 값 자체가 "올랐나 내렸나" 라
                        여기서는 등락색이 맞다 - 아래 추이선은 반대로 등락색을 쓰지 않는다
                        (계열이 하나라 선 색이 지금 상태로 오독된다. RateChart 주석)
                    */}
                    <dd className={cx('tabular-nums', changeToneClass(summary.change))}>
                      {describeChange(summary.change, summary.changeRate)}
                    </dd>
                  </div>
                  <div className="flex flex-wrap gap-x-2">
                    <dt className="shrink-0 text-fg-muted">{fxRangeSpan(range)}</dt>
                    <dd className="tabular-nums">
                      {formatRate(summary.min)} ~ {formatRate(summary.max)}
                    </dd>
                  </div>
                </dl>
              )}
            </div>
          )}
        </Card>

        <Calculator rate={rate} />
      </div>

      <Card title="환율 추이">
        {/*
          🔴 기간 버튼을 카드 헤더(action)가 아니라 본문에 둔다 (2026-09-20, I-66)
            버튼이 둘(7일 · 30일)일 때는 제목 옆에 들어갔는데 여섯이 되면서 넘쳤다.
            카드 헤더는 공유 부품(components/ui Card)의 flex 한 줄이라 접히지 않는다.
            본문에서 flex-wrap 으로 두면 폰에서 두 줄로 접힌다.
        */}
        <div className="mb-3 flex flex-wrap gap-1" role="tablist" aria-label="추이 기간">
          {FX_RANGES.map((days) => (
            <button
              key={days}
              type="button"
              role="tab"
              aria-selected={range === days}
              onClick={() => setRange(days)}
              className={cx(
                'min-h-[32px] rounded-full px-3 py-1 text-sm',
                range === days ? 'bg-brand text-brand-fg' : 'bg-surface-hover text-fg-muted',
              )}
            >
              {fxRangeLabel(days)}
            </button>
          ))}
        </div>

        {history.isPending ? (
          <Spinner />
        ) : history.isError ? (
          <ErrorState message="환율 기록을 불러오지 못했습니다." onRetry={() => history.refetch()} />
        ) : history.data.length === 0 ? (
          <EmptyState title="이 기간의 기록이 없습니다" />
        ) : (
          <RateChart history={history.data} />
        )}
        {/*
          🔴 두 출처가 한 선에 섞인다 (2026-09-20, I-66)
            토스는 지금 환율만 주고 우리 이력은 2026-08-20 21시부터다.
            그 앞 구간은 한국은행 일별 매매기준율로 채웠다. 성격이 다른 값이라
            화면이 그 사실을 말해야 한다 - 안 적으면 "왜 옛날은 하루 한 점이지" 가 된다.
        */}
        <p className="mt-2 text-xs text-fg-subtle">
          {range > 30
            ? '30일이 넘는 기간은 하루에 한 점(그날 마지막 값)으로 그립니다. '
            : '매시 정각에 기록한 값입니다. '}
          점을 누르거나 올리면 그 시각 환율이 보입니다.
          {(history.data ?? []).some((point) => point.source === 'ECOS') && (
            <>
              {' '}2026년 8월 20일 이전 구간은 <strong>한국은행 일별 매매기준율</strong>입니다 —
              토스증권 참고 환율과 조금 다를 수 있습니다.
            </>
          )}
        </p>
      </Card>
    </div>
  );
}

/**
 * 등락 글자색. 오르면 빨강 · 내리면 파랑 · 그대로면 기본색. (2026-09-20)
 *
 * priceDisplay.ts 의 시세 등락색과 같은 토큰을 쓴다 - 같은 뜻을 두 색으로 말하면 안 된다.
 */
function changeToneClass(change: number): string {
  if (change > 0) {
    return 'text-rise';
  }
  return change < 0 ? 'text-fall' : '';
}

/** "+6.90원 (+0.50%) · 달러값이 올랐습니다" - 비전문가가 부호만 보고 뜻을 헷갈리지 않게 말로 붙인다 */
function describeChange(change: number, changeRate: number): string {
  const sign = change > 0 ? '+' : '';
  const words = change > 0 ? '달러값이 올랐습니다' : change < 0 ? '달러값이 내렸습니다' : '그대로입니다';
  return `${sign}${formatRate(change)} (${sign}${changeRate.toFixed(2)}%) · ${words}`;
}

/**
 * 달러 ↔ 원 계산기.
 *
 * 🔴 마지막으로 적은 칸이 기준이다
 *   두 칸을 각각 상태로 두면 환율이 1분마다 바뀔 때 어느 쪽을 다시 계산할지 모호해진다.
 *   "무엇을 적었나" 하나만 들고, 다른 칸은 그때그때 지금 환율로 계산한다.
 */
function Calculator({ rate }: { rate: number | null }) {
  const [input, setInput] = useState<{ from: 'usd' | 'krw'; value: string }>({
    from: 'usd',
    value: '100',
  });

  const typed = input.value === '' ? null : Number(input.value);
  let usdText = '';
  let krwText = '';
  if (input.from === 'usd') {
    usdText = input.value;
    const krw = rate !== null && typed !== null ? usdToKrw(typed, rate) : null;
    krwText = krw === null ? '' : krw.toLocaleString('ko-KR');
  } else {
    krwText = toMoneyDisplay(input.value);
    const usd = rate !== null && typed !== null ? krwToUsd(typed, rate) : null;
    usdText = usd === null ? '' : usd.toFixed(2);
  }

  return (
    <Card title="달러 ↔ 원 계산기">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-fg-muted">달러 (USD)</span>
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            disabled={rate === null}
            value={usdText}
            onChange={(e) => setInput({ from: 'usd', value: toUsdInput(e.target.value) })}
            className={cx(FIELD_CLASS, 'tabular-nums')}
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-fg-muted">원 (KRW)</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            disabled={rate === null}
            value={krwText}
            onChange={(e) => setInput({ from: 'krw', value: toMoneyValue(e.target.value) })}
            className={cx(FIELD_CLASS, 'tabular-nums')}
          />
        </label>
      </div>
      <p className="mt-2 text-xs text-fg-subtle">
        {rate === null
          ? '환율이 있어야 계산할 수 있습니다.'
          : `지금 환율 ${formatRate(rate)}로 계산합니다. 환전 수수료는 빠져 있습니다.`}
      </p>
    </Card>
  );
}

/**
 * 추이선 한 줄. (dataviz 규칙 - 한 계열이라 범례 없음 · 2px · 세로 안내선 + 툴팁)
 *
 * 색은 모의투자 차트의 안전 프로필 파랑(--paper-safe)을 빌린다 - 계열이 하나라 정체성 색이
 * 무엇이든 되지만, 새 토큰을 만들지 않고 라이트·다크가 이미 검증된 값을 쓴다.
 * 🔴 --rise/--fall(빨강·파랑)은 쓰지 않는다 - "올랐나 내렸나" 로 읽힌다.
 */
function RateChart({ history }: { history: Parameters<typeof toChartRows>[0] }) {
  const c = usePaperChartColors();
  const rows = toChartRows(history);
  const rates = rows.map((r) => r.rate);
  const min = Math.min(...rates);
  const max = Math.max(...rates);
  // 위아래 여유 - 폭의 10% 와 1원 중 큰 쪽. 하루 변동이 작으면 선이 천장·바닥에 붙는다
  const pad = Math.max((max - min) * 0.1, 1);

  return (
    <div className="h-56 w-full sm:h-72">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={c.line} vertical={false} />
          <XAxis
            dataKey="at"
            // 날이 바뀌는 점만 눈금으로 - 날짜를 축 값으로 쓰면 같은 날이 두 번 찍혔다 (dayTicks)
            ticks={dayTicks(rows, 8)}
            interval={0}
            tickFormatter={(at: string) => at.slice(0, 5)}
            tick={{ fill: c.axis, fontSize: 11 }}
            stroke={c.line}
          />
          <YAxis
            domain={[Math.floor(min - pad), Math.ceil(max + pad)]}
            tick={{ fill: c.axis, fontSize: 11 }}
            stroke={c.line}
            width={48}
            tickFormatter={(v: number) => v.toLocaleString('ko-KR')}
          />
          <Tooltip
            cursor={{ stroke: c.axis, strokeDasharray: '3 3' }}
            labelFormatter={(at) => String(at)}
            formatter={(value) => [formatRate(Number(value)), '1달러']}
            contentStyle={{
              background: c.surface,
              border: `1px solid ${c.line}`,
              borderRadius: 8,
              fontSize: 12,
            }}
          />
          <Line
            dataKey="rate"
            stroke={c.safe}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, stroke: c.surface, strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
